import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import {
  getErrorLoadingIndividualByUserId,
  getIndividualByUserId,
  getLoadingIndividualUserIds,
  populateIndividualsByUserIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";

const StyledLink = styled(Link)(({ theme }) => ({
  alignItems: "center",
  color: theme.palette.common.black,
  cursor: "pointer",
  display: "flex",
  overflow: "hidden",
  textDecoration: "underline",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type CreatorLinkProps = {
  creator: EntityMember;
};

export default function CreatorLink(props: Readonly<CreatorLinkProps>) {
  const { creator } = props;

  const individual = useSelector(getIndividualByUserId(creator.userId));
  const loadingIndividuals = useSelector(getLoadingIndividualUserIds());
  const loadingCreator = loadingIndividuals.some((id) => id.isEqualTo(creator.userId));
  const errorLoadingCreator = useSelector(getErrorLoadingIndividualByUserId(individual?.userId));
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();

  useEffect(() => {
    if (!individual && !loadingCreator && !errorLoadingCreator) {
      dispatch(populateIndividualsByUserIds({ session, userIds: [creator.userId] }));
    }
  }, [session, dispatch, individual, loadingCreator, errorLoadingCreator, creator.userId]);

  const { openDialog, closeDialog } = useDialog();

  function openProfileDialog(individualId: Guid) {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      contentSxProps: {
        display: "flex",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
      titleStyle: {
        paddingBottom: 0
      },
      onClose: closeDialog,
    });
  }

  if (loadingCreator) return <StyledLink>Loading...</StyledLink>;
  if (errorLoadingCreator) return <StyledLink>Error loading user</StyledLink>;
  if (!individual) return <StyledLink>Unknown user</StyledLink>;

  return (
    <StyledLink
      onClick={(event) => {
        event.stopPropagation();
        openProfileDialog(individual.id);
      }}
    >
      {`${individual.fullName}`}
    </StyledLink>
  );
}
